"use client"

import { getAuthHeader } from "../utils/auth";
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useOutletContext, Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { formatJstDateTime, englishStatusLabel, isEnglishOperatorMode, withOperatorSurface } from "../utils/operatorMode";

// --- SVG Icons ---
const EditIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>;
const CloseIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>;
const SaveIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2 2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path><polyline points="17 21 17 13 7 13 7 21"></polyline><polyline points="7 3 7 8 15 8"></polyline></svg>;
const SearchIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>;
const LockIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-slate-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>;
const SparklesIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m12 3-1.9 3.8-3.8 1.9 3.8 1.9L12 14l1.9-3.8 3.8-1.9-3.8-1.9L12 3Z"/><path d="M5 3v4"/><path d="M19 17v4"/><path d="M3 5h4"/><path d="M17 19h4"/></svg>;
const DocumentTextIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 18.5a.5.5 0 0 1-.5.5h-10a.5.5 0 0 1-.5-.5v-13a.5.5 0 0 1 .5-.5h7.5L17 8.5v10Z"/><path d="M14 3v5.5h5.5"/><path d="M10 16.5h4"/><path d="M10 13.5h4"/></svg>;
const PlusIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>;
const TrashIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>;
const LinkIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path></svg>;
const ClipboardIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path><rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect></svg>;
const UserIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>;
const EyeIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>;


const LoadingSpinner = ({ text = "読み込み中..." }) => (
    <div className="flex items-center text-lg font-semibold text-gray-700">
        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        {text}
    </div>
);

// --- Helper & UI Components ---
const API_URL = window.location.hostname === 'localhost' 
  ? (process.env.REACT_APP_API_URL || "http://localhost:3173") 
  : window.location.origin;

const AI_SALES_CONSULTATION_ENABLED = false;

const DEAL_STATUSES = ["未設定", "アポ", "商談", "失注", "クレーム", "受注"];
const DEAL_STATUS_LABELS_EN = {
    "未設定": "Needs Triage",
    "アポ": "Meeting",
    "商談": "In Conversation",
    "失注": "Lost",
    "クレーム": "Issue",
    "受注": "Won",
};

const CALL_STATUSES = ["不通", "留守電", "受付NG", "受付資料請求", "担当不在", "担当NG", "担当資料請求", "担当時期先", "アポイント", "その他", "🔒 NGリストに追加"];
const QUICK_CALL_STATUSES = CALL_STATUSES.filter((status) => status !== "🔒 NGリストに追加");
const CALL_STATUS_LABELS_EN = {
    "不通": "No Answer",
    "留守電": "Voicemail",
    "受付NG": "Gatekeeper Rejected",
    "受付資料請求": "Info Requested by Reception",
    "担当不在": "Decision Maker Unavailable",
    "担当NG": "Decision Maker Rejected",
    "担当資料請求": "Info Requested",
    "担当時期先": "Later Timing",
    "アポイント": "Meeting Booked",
    "その他": "Other",
    "🔒 NGリストに追加": "Add to Suppression",
};

const SEND_STATUS_OPTIONS_JA = [
    { value: "all_sent", label: "送信済みすべて" },
    { value: "all_client_list", label: "すべて (マイリスト)" },
    { value: "success", label: "成功" },
    { value: "needs_review", label: "要確認" },
    { value: "fail", label: "失敗" },
];

const SEND_STATUS_OPTIONS_EN = [
    { value: "all_sent", label: "All Submitted Targets" },
    { value: "success", label: "Submitted" },
    { value: "needs_review", label: "Needs Review" },
    { value: "fail", label: "Failed" },
];

const getEnglishBetaDealFilters = (filters) => ({
    ...filters,
    send_status: !filters.send_status || filters.send_status === "all_client_list" ? "all_sent" : filters.send_status,
    listTypeFilter: "master",
});

const getDealStatusLabel = (status, isEnglishBeta) => {
    if (!isEnglishBeta) return status || "未設定";
    return DEAL_STATUS_LABELS_EN[status] || status || "Needs Triage";
};

const getCallStatusLabel = (status, isEnglishBeta) => {
    if (!isEnglishBeta) return status || "未選択";
    return CALL_STATUS_LABELS_EN[status] || status || "Not Set";
};

const getSourceLabel = (listType, isEnglishBeta) => {
    if (!isEnglishBeta) return listType === "client" ? "マイリスト" : "弊社提供";
    return listType === "client" ? "Client List" : "SalesBreaker DB";
};

const formatDateInputValue = (value) => {
    if (!value) return "";
    if (typeof value === "string") return value.slice(0, 10);
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toISOString().slice(0, 10);
};

function Pagination({ currentPage, totalPages, onPageChange, isLoading, disabled, isEnglishBeta = false }) {
    if (totalPages <= 1) return null;
    return (
        <div className="flex items-center justify-center space-x-2">
            <button onClick={() => onPageChange(1)} disabled={currentPage === 1 || isLoading || disabled} className="px-3 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">{isEnglishBeta ? "First" : "最初"}</button>
            <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1 || isLoading || disabled} className="px-3 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">{isEnglishBeta ? "Previous" : "前へ"}</button>
            <span className="text-sm text-gray-700">{currentPage} / {totalPages}</span>
            <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages || isLoading || disabled} className="px-3 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">{isEnglishBeta ? "Next" : "次へ"}</button>
            <button onClick={() => onPageChange(totalPages)} disabled={currentPage === totalPages || isLoading || disabled} className="px-3 py-1 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">{isEnglishBeta ? "Last" : "最後"}</button>
        </div>
    );
}

function UpgradeModal({ onClose, user, isEnglishBeta = false, accountPath = '/account' }) {
    return (
        <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={onClose}
        >
            <div 
                className="bg-white p-8 rounded-lg shadow-2xl border w-full max-w-md text-center relative"
                onClick={e => e.stopPropagation()}
            >
                <button 
                    onClick={onClose} 
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                    aria-label={isEnglishBeta ? "Close" : "閉じる"}
                >
                    <CloseIcon />
                </button>
                
                <LockIcon />
                
                <h3 className="text-xl font-bold text-slate-800 mt-2">
                    {isEnglishBeta ? "This action is outside the English beta shell" : "この機能は有料プランでご利用いただけます"}
                </h3>
                <p className="text-slate-600 mt-4 text-sm leading-relaxed">
                    {isEnglishBeta
                        ? "English Operator Beta exposes the follow-up pipeline only. Deeper CRM, quotation, and AI sales-consulting tools remain outside this limited beta surface."
                        : "全期間の履歴閲覧、ページ遷移、CSVダウンロード、AI・見積書機能の利用には、プランのアップグレードが必要です。"}
                </p>
                
                {/* OEMユーザーには本家アカウントページへのリンクを出さない。 */}
                {!user?.oem_id ? (
                    <Link 
                        to={isEnglishBeta ? withOperatorSurface(accountPath) : accountPath} 
                        className="mt-8 inline-block bg-green-600 text-white font-bold py-3 px-8 rounded-lg hover:bg-green-700 transition-colors w-full"
                    >
                        {isEnglishBeta ? "Open Account" : "🚀 プランをアップグレードする"}
                    </Link>
                ) : (
                    <div className="mt-8 p-4 bg-blue-50 text-blue-700 font-bold rounded-xl border border-blue-100">
                        {isEnglishBeta ? "Contact your account owner for access questions." : "アップグレードについては、ご契約先の担当者までお問い合わせください。"}
                    </div>
                )}
            </div>
        </div>
    );
}

const StatusBadge = ({ status, isEnglishBeta = false }) => {
    const lowerStatus = status ? String(status).toLowerCase() : '';

    if (lowerStatus === 'success') {
        return <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">{isEnglishBeta ? englishStatusLabel(status) : "✅ 成功"}</span>;
    }
    if (lowerStatus === 'needs_review') {
        return <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">{isEnglishBeta ? englishStatusLabel(status) : "⚠️ 要確認"}</span>;
    }
    if (lowerStatus.startsWith('fail')) {
        return <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">{isEnglishBeta ? englishStatusLabel(status) : "❌ 失敗"}</span>;
    }
    return <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">{isEnglishBeta ? englishStatusLabel(status) : "記録なし"}</span>;
};

const initialQuotationForm = {
    customer_name: "",
    issuer_name: "",
    issuer_address: "",
    issuer_phone: "",
    issuer_staff_name: "",
    notes: "",
};

// --- Main Component ---
function DealsPage({ accountPath = '/account' }) {
    const { user } = useOutletContext();
    const location = useLocation();
    const navigate = useNavigate();
    const isEnglishBeta = isEnglishOperatorMode();
    
    const [searchParams] = useSearchParams();
    const querySearch = searchParams.get('searchTerm') || "";
    const shouldSearchFromUrl = Boolean(querySearch);

    // --- List Data States ---
    const [list, setList] = useState([]);
    const [pagination, setPagination] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    
    // --- Tab & Filter States (Team Feature) ---
    const [viewMode, setViewMode] = useState("all");

    const [filters, setFilters] = useState({ 
        status: "", angle: "", call_status: "", 
        searchTerm: querySearch, 
        hasClick: "", sales_staff_email: "", 
        send_status: "all_sent",
        listTypeFilter: ""
    });
    const [activeFilters, setActiveFilters] = useState(filters);

    // --- Modal & Edit States ---
    const [editingItem, setEditingItem] = useState(null);
    const [modalDetails, setModalDetails] = useState(null);
    const [isModalLoading, setIsModalLoading] = useState(false);
    const [form, setForm] = useState({});
    const [activeModalTab, setActiveModalTab] = useState("result");

    // --- Bulk Action States (Team Feature) ---
    const [selectedIds, setSelectedIds] = useState(new Set()); 
    const [isBulkProcessing, setIsBulkProcessing] = useState(false);

    // --- Common States ---
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(false); 
    const [initialLoading, setInitialLoading] = useState(true);
    // 通常表示は未検索。URL検索またはMyPageのホットリード遷移だけ自動検索する。
    const [hasSearched, setHasSearched] = useState(shouldSearchFromUrl);
    const [isFreePlan, setIsFreePlan] = useState(false);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);

    // --- AI / Quotation States ---
    const [aiScript, setAiScript] = useState("");
    const [isAiLoading, setIsAiLoading] = useState(false);
    const [aiError, setAiError] = useState(null);
    const [isSavingHistory, setIsSavingHistory] = useState(false);
    const [aiConsultationCount, setAiConsultationCount] = useState(0);
    const [saveStatus, setSaveStatus] = useState(null); 
    const [aiSaveStatus, setAiSaveStatus] = useState(null);
    const [clientProducts, setClientProducts] = useState([]); 
    const [selectedProductId, setSelectedProductId] = useState("");

    const [quotationHistory, setQuotationHistory] = useState([]); 
    const [isQuotationLoading, setIsQuotationLoading] = useState(false);
    const [isGeneratingQuotation, setIsGeneratingQuotation] = useState(false); 
    const [showQuotationForm, setShowQuotationForm] = useState(false);
    const [quotationForm, setQuotationForm] = useState(initialQuotationForm);
    const [quotationItems, setQuotationItems] = useState([{ id: 1, name: "", quantity: 1, unit_price: 0 }]);
    const [generatedQuotationUrl, setGeneratedQuotationUrl] = useState(null);
    const [copySuccess, setCopySuccess] = useState('');
    
    const [webTimeline, setWebTimeline] = useState([]);
    const [isWebTimelineLoading, setIsWebTimelineLoading] = useState(false);


    const selectedClientId = useMemo(() => (user?.role === "admin" || user?.role === "admin_staff" ? localStorage.getItem("selectedClientId") || "" : user?.client_id), [user]);

    // データ取得
    const fetchDeals = useCallback(async (page, currentActiveFilters) => {
        if (!user || !selectedClientId) return;
        
        setIsLoading(true);
        // ページ切り替え時は選択状態をリセット（誤操作防止）
        setSelectedIds(new Set());

        const requestFilters = isEnglishBeta ? getEnglishBetaDealFilters(currentActiveFilters) : currentActiveFilters;
        const params = new URLSearchParams({
            page, limit: 50, client_id: selectedClientId,
            role: user.role, email: user.email, ...requestFilters
        });

        try {
            const res = await fetch(`${API_URL}/api/deals?${params.toString()}`, {
                headers: getAuthHeader(),
            });
            const result = await res.json();
            if (result.success) {
                setList(result.data || []);
                setPagination(result.pagination || null);
            } else {
                console.error("案件リストの読み込みに失敗:", result.error);
                setList([]); setPagination(null);
            }
        } catch(err) {
            console.error("案件リストの取得エラー:", err);
        } finally {
            setIsLoading(false);
        }
    }, [user, selectedClientId, isEnglishBeta]);

    // ユーザー一覧取得
    const fetchUsers = useCallback(() => {
        if (!selectedClientId) return;
        fetch(`${API_URL}/api/users?client_id=${selectedClientId}`, {
            headers: getAuthHeader(),
        }).then(res => res.json())
          .then(usersData => { if (usersData.success) setUsers(usersData.users || []); })
          .catch(err => console.error("ユーザーデータの取得エラー:", err));
    }, [selectedClientId]);
    
    // 初期化
    useEffect(() => {
        if (!selectedClientId) {
            setInitialLoading(false);
            return;
        }
        const fetchInitialData = async () => {
            setInitialLoading(true);
            try {
                const res = await fetch(`${API_URL}/api/subscription-status?clientId=${selectedClientId}`, {
                    headers: getAuthHeader(),
                });
                if (!res.ok) throw new Error('プラン情報の取得に失敗しました。');
                const data = await res.json();
                if (data.success) {
                    const isFree = !data.subscription || data.subscription.plan_id_string === 'free';
                    setIsFreePlan(isFree);
                }
                fetchUsers();
            } catch (error) {
                console.error("初期データの取得に失敗:", error);
            } finally {
                setInitialLoading(false);
            }
        };
        fetchInitialData();
    }, [selectedClientId, fetchUsers]);

    // MyPageのホットリード通知から来た場合だけ、自動でフィルタ検索する。
    useEffect(() => {
        if (location.state?.autoFilter && !initialLoading) {
            const autoFilter = isEnglishBeta ? getEnglishBetaDealFilters(location.state.autoFilter) : location.state.autoFilter;
            
            setFilters(prev => ({ ...prev, ...autoFilter }));
            setActiveFilters(prev => ({ ...prev, ...autoFilter }));
            
            setHasSearched(true);
            setCurrentPage(1);
            
            navigate('.', { replace: true, state: {} });
        }
    }, [location.state, initialLoading, navigate, isEnglishBeta]);

    // 未検索の初期表示では重い一覧取得を走らせない。
    useEffect(() => {
        if (hasSearched && !initialLoading) {
            fetchDeals(currentPage, activeFilters);
        }
    }, [currentPage, activeFilters, fetchDeals, hasSearched, initialLoading]);

    const getUserNameByEmail = useCallback((email) => {
        return users.find(u => u.email === email)?.name || email || 'ー';
    }, [users]);

    // --- 一括操作ロジック (Team Feature) ---
    const handleCheckboxChange = (id, listType) => {
        const key = `${id}-${listType}`;
        setSelectedIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(key)) newSet.delete(key);
            else newSet.add(key);
            return newSet;
        });
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const newSet = new Set();
            list.forEach(item => newSet.add(`${item.id}-${item.list_type}`));
            setSelectedIds(newSet);
        } else {
            setSelectedIds(new Set());
        }
    };

    // 一括更新API呼び出し
    const handleBulkUpdate = async (updates) => {
        if (selectedIds.size === 0) return;
        if (!window.confirm(isEnglishBeta ? `Update ${selectedIds.size} selected records?` : `選択した${selectedIds.size}件を更新しますか？`)) return;

        setIsBulkProcessing(true);
        try {
            const items = Array.from(selectedIds).map(key => {
                const [idStr, type] = key.split('-');
                const id = parseInt(idStr, 10);
                const listItem = list.find(l => l.id === id && l.list_type === type);
                return {
                    sales_list_id: id,
                    list_type: type,
                    deal_id: listItem?.deal_id || null,
                    company: listItem?.company
                };
            });

            const res = await fetch(`${API_URL}/api/deals/bulk-update`, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...getAuthHeader() },
                body: JSON.stringify({
                    items,
                    updates,
                    client_id: selectedClientId,
                    staff_email: user.email
                })
            });

            const data = await res.json();
            if (data.success) {
                alert(isEnglishBeta ? `${data.count} records updated.` : `${data.count}件の更新が完了しました。`);
                setSelectedIds(new Set());
                fetchDeals(currentPage, activeFilters);
            } else {
                throw new Error(data.error);
            }
        } catch (err) {
            console.error("一括更新エラー:", err);
            alert(isEnglishBeta ? `Update failed: ${err.message}` : `更新に失敗しました: ${err.message}`);
        } finally {
            setIsBulkProcessing(false);
        }
    };

    // --- クイック更新ロジック ---
    const handleQuickUpdate = async (item, field, value) => {
        if (!item.deal_id) {
            try {
                const res = await fetch(`${API_URL}/api/deals/bulk-update`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", ...getAuthHeader() },
                    body: JSON.stringify({
                        items: [{
                            sales_list_id: item.id,
                            list_type: item.list_type,
                            deal_id: null,
                            company: item.company
                        }],
                        updates: { [field]: value },
                        client_id: selectedClientId,
                        staff_email: user.email
                    })
                });
                const data = await res.json();
                if (data.success) {
                    fetchDeals(currentPage, activeFilters);
                } else {
                    alert(isEnglishBeta ? `Update failed: ${data.error}` : `更新失敗: ${data.error}`);
                }
            } catch (err) {
                console.error("Quick Create Update Error:", err);
            }
            return;
        }

        try {
            setList(prevList => prevList.map(row => {
                if (row.deal_id === item.deal_id) {
                    return { ...row, [field]: value };
                }
                return row;
            }));

            const res = await fetch(`${API_URL}/api/deals/quick-update`, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...getAuthHeader() },
                body: JSON.stringify({
                    id: item.deal_id,
                    field,
                    value,
                    client_id: selectedClientId,
                    company: item.company,
                    staff_email: user.email
                })
            });
            
            const data = await res.json();
            if (!data.success) {
                alert(isEnglishBeta ? `Update failed: ${data.error}` : `更新に失敗しました: ${data.error}`);
                fetchDeals(currentPage, activeFilters);
            }
        } catch (err) {
            console.error("Quick Update Error:", err);
            fetchDeals(currentPage, activeFilters);
        }
    };

    // --- 詳細モーダル関連 ---
    const fetchQuotationHistory = useCallback(async () => {
        if (!editingItem?.deal_id) {
            setQuotationHistory([]);
            return;
        }
        setIsQuotationLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/deals/${editingItem.deal_id}/quotations`, {
                headers: getAuthHeader(),
            });
            const data = await res.json();
            if (data.success) setQuotationHistory(data.quotations || []);
        } catch (err) { console.error(err); } 
        finally { setIsQuotationLoading(false); }
    }, [editingItem]);

    const fetchSavedAiScript = useCallback(async () => {
        if (!editingItem) return;
        setIsAiLoading(true); setAiError(null); setAiSaveStatus(null);
        try {
            const params = new URLSearchParams({
                deal_id: editingItem.deal_id || null,
                client_id: selectedClientId,
                sales_list_id: editingItem.id,
                list_type: editingItem.list_type
            });
            const res = await fetch(`${API_URL}/api/deals/ai-script?${params.toString()}`, { headers: getAuthHeader() });
            const data = await res.json();
            if (data.success) setAiScript(data.script_content || "");
        } catch (err) { setAiError("保存済みスクリプトの読込失敗"); } 
        finally { setIsAiLoading(false); }
    }, [editingItem, selectedClientId]);

    const fetchClientProducts = useCallback(async () => {
        if (!selectedClientId) return;
        try {
            const res = await fetch(`${API_URL}/api/client/products?client_id=${selectedClientId}`, { headers: getAuthHeader() });
            const data = await res.json();
            if (data.success) {
                setClientProducts(data.products || []);
                if (data.products?.length > 0) setSelectedProductId(data.products[0].id);
            }
        } catch (err) { console.error(err); }
    }, [selectedClientId]);

    const fetchWebTimeline = useCallback(async (salesListId) => {
        if (!salesListId) { setWebTimeline([]); return; }
        setIsWebTimelineLoading(true);
        try {
            const params = new URLSearchParams({ 
                client_id: selectedClientId,
                list_type: editingItem?.list_type || 'client'
            });
            const res = await fetch(`${API_URL}/api/deals/${salesListId}/web-timeline?${params.toString()}`, { headers: getAuthHeader() });
            const data = await res.json();
            if (data.success) setWebTimeline(data.logs || []);
        } catch (err) {
            console.error("Webタイムライン取得失敗:", err);
            setWebTimeline([]);
        } finally {
            setIsWebTimelineLoading(false);
        }
    }, [selectedClientId, editingItem?.list_type]);

    useEffect(() => {
        if (!editingItem) return;

        if ((!AI_SALES_CONSULTATION_ENABLED && activeModalTab === 'ai_consult') || (isEnglishBeta && ['ai_consult', 'quotation', 'web_timeline'].includes(activeModalTab))) {
            setActiveModalTab("result");
            return;
        }
        
        switch (activeModalTab) {
            case 'quotation':
                fetchQuotationHistory();
                setShowQuotationForm(false);
                break;
            case 'ai_consult':
                fetchSavedAiScript();
                fetchClientProducts();
                break;
            case 'web_timeline':
                // DealsにはリードID(lid)がないため、一旦 deals の sales_list_id (マイリストID) をリードIDとして流用
                // ※ Dealsのsales_list_idは、MAリードからの案件化時に、MarketingLeadsのIDではなくClientSalesListsのIDになるため、
                //    ClientSalesListsのIDを MarketingLeadsのIDとして代用する。
                if (editingItem.list_type === 'client' && editingItem.id) {
                    fetchWebTimeline(editingItem.id); 
                } else {
                    setWebTimeline([]);
                }
                break;
            default:
                break;
        }
    }, [activeModalTab, editingItem, fetchQuotationHistory, fetchSavedAiScript, fetchClientProducts, fetchWebTimeline, isEnglishBeta]);


    const handleEditClick = async (item) => {
        setEditingItem(item);
        setIsModalLoading(true);
        setModalDetails(null);
        setActiveModalTab("result");
        setAiScript("");
        setIsAiLoading(false);
        setAiError(null);
        setSaveStatus(null);
        setWebTimeline([]);
        
        setQuotationHistory([]); setShowQuotationForm(false); setGeneratedQuotationUrl(null);

        try {
            if (!item.deal_id) {
                setModalDetails({
                    details: { ...item, sales_list_id: item.id, status: "未設定", angle: "", call_status: "", comment: "", sales_staff_email: user.email, cs_staff_email: "", next_action_date: "", next_action_note: "" },
                    sendHistory: [], clickLogs: [], dealHistory: []
                });
                setForm({ status: "未設定", angle: "", call_status: "", comment: "", sales_staff_email: user.email, cs_staff_email: "", next_action_date: "", next_action_note: "" });
                setAiConsultationCount(0);
                
                if (item.has_click) {
                    setActiveModalTab(!isEnglishBeta && item.list_type === 'client' ? "web_timeline" : "clicks");
                }
                setIsModalLoading(false);
                return;
            }

            const res = await fetch(`${API_URL}/api/deals/details/${item.deal_id}?client_id=${selectedClientId}`, { headers: getAuthHeader() });
            const result = await res.json();

            if (result.success) {
                setModalDetails(result);
                setForm({
                    status: result.details.status || "未設定", angle: result.details.angle || "",
                    call_status: result.details.call_status || "", comment: result.details.comment || "",
                    sales_staff_email: result.details.sales_staff_email || user.email,
                    cs_staff_email: result.details.cs_staff_email || "",
                    next_action_date: formatDateInputValue(result.details.next_action_date),
                    next_action_note: result.details.next_action_note || ""
                });
                const aiCount = (result.dealHistory || []).filter(h => h.comment && h.comment.startsWith('[AI営業相談]')).length;
                setAiConsultationCount(aiCount);

                if (AI_SALES_CONSULTATION_ENABLED && !isEnglishBeta && item.has_click && (result.details.status === '未設定' || !result.details.status)) {
                    setActiveModalTab("ai_consult");
                    fetchSavedAiScript();
                    fetchClientProducts();
                }
                
                if (item.has_click) {
                     setActiveModalTab(isEnglishBeta ? "clicks" : "web_timeline"); 
                }

            } else {
                throw new Error(result.error);
            }
        } catch (err) {
            setAiError(isEnglishBeta ? `Could not load pipeline detail: ${err.message}` : `詳細情報の取得に失敗: ${err.message}`);
        } finally {
            setIsModalLoading(false);
        }
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };
    
    const handleSave = async (e) => {
        e.preventDefault();
        if (!editingItem) return;
        
        const lastUpdatedAt = modalDetails?.details?.updated_at || null;

        const payload = { 
            ...form, 
            company: editingItem.company, 
            client_id: selectedClientId, 
            staff_email: user.email, 
            sales_list_id: editingItem.id,
            list_type: editingItem.list_type,
            last_updated_at: lastUpdatedAt 
        };

        try {
            const res = await fetch(`${API_URL}/api/deals/save`, { 
                method: "POST", 
                headers: {"Content-Type": "application/json", ...getAuthHeader()},
                body: JSON.stringify(payload)});
            
            if (res.status === 409) {
                const conflictMessage = isEnglishBeta
                    ? "This record was updated by another operator. Reload the latest data instead of overwriting it? Choose Cancel to force-save your changes."
                    : "【警告】他のユーザーによってデータが更新されています。\n上書きせずに最新データを読み込みますか？\n(キャンセルを押すと強制的に上書き保存を試みます)";
                if (window.confirm(conflictMessage)) {
                    handleEditClick(editingItem); 
                    return;
                }
                const forcePayload = { ...payload };
                delete forcePayload.last_updated_at;
                const retryRes = await fetch(`${API_URL}/api/deals/save`, { 
                    method: "POST", headers: {"Content-Type": "application/json", ...getAuthHeader()}, body: JSON.stringify(forcePayload)});
                if(!retryRes.ok) throw new Error(isEnglishBeta ? "Force save failed." : "強制保存に失敗しました");
            } else if (!res.ok) {
                throw new Error(isEnglishBeta ? "Save failed." : "保存に失敗しました。");
            }
            
            setSaveStatus({ type: 'success', message: isEnglishBeta ? "Pipeline update saved." : "案件情報を更新しました。" });
            fetchDeals(currentPage, activeFilters);

            if (editingItem.deal_id || (await res.clone().json()).deal_id) {
                const resData = await res.clone().json();
                const targetDealId = editingItem.deal_id || resData.deal_id;
                
                const refreshRes = await fetch(`${API_URL}/api/deals/details/${targetDealId}?client_id=${selectedClientId}`, { headers: getAuthHeader() });
                const refreshResult = await refreshRes.json();
                if (refreshResult.success) {
                    setModalDetails(refreshResult);
                    setForm({
                        status: refreshResult.details.status || "未設定",
                        angle: refreshResult.details.angle || "",
                        call_status: refreshResult.details.call_status || "",
                        comment: refreshResult.details.comment || "",
                        sales_staff_email: refreshResult.details.sales_staff_email || user.email,
                        cs_staff_email: refreshResult.details.cs_staff_email || "",
                        next_action_date: formatDateInputValue(refreshResult.details.next_action_date),
                        next_action_note: refreshResult.details.next_action_note || ""
                    });
                    setEditingItem(prev => ({...prev, deal_id: targetDealId}));
                }
            }
        } catch (err) { 
            setSaveStatus({ type: 'error', message: err.message });
        }
    };

    const handleGenerateAiScript = async () => {
        if (!modalDetails || !modalDetails.details) return;

        if (isFreePlan) {
            setShowUpgradeModal(true);
            return;
        }
        
        if (!selectedProductId) {
            setAiError("AIに相談する「商品」を選択してください。");
            return;
        }

        if (aiConsultationCount >= 3) {
            setAiError("AI相談の回数が上限（1案件につき3回）に達しました。");
            return;
        }

        setIsAiLoading(true);
        setAiError(null);
        setAiSaveStatus(null);
        setAiScript("");

        try {
            const payload = {
                sales_list_id: modalDetails.details.sales_list_id,
                list_type: modalDetails.details.list_type,
                client_id: selectedClientId,
                product_id: selectedProductId
            };

            const res = await fetch(`${API_URL}/api/ai/generate-sales-script`, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...getAuthHeader() },
                body: JSON.stringify(payload)
            });

            if (!res.ok) {
                const errorText = await res.text();
                throw new Error(errorText || "AIサービスへの接続に失敗しました。");
            }
            
            const reader = res.body.getReader();
            const decoder = new TextDecoder("utf-8");
            let buffer = "";

            while (true) {
                const { done, value } = await reader.read();
                if (done) {
                    setAiConsultationCount(prevCount => prevCount + 1);
                    break;
                }
                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split("\n\n");
                buffer = lines.pop();

                for (const line of lines) {
                    if (line.startsWith("data: ")) {
                        const dataStr = line.substring(6);
                        try {
                            const data = JSON.parse(dataStr);
                            if (data.content) setAiScript(prev => prev + data.content);
                            if (data.error) throw new Error(data.message);
                            if (data.finished) break;
                        } catch (e) { console.error(e); }
                    }
                }
            }
        } catch (err) {
            setAiError(`AIスクリプトの生成に失敗しました: ${err.message}`);
        } finally {
            setIsAiLoading(false);
        }
    };

    const handleSaveAiScript = async () => {
        if (isFreePlan) { setShowUpgradeModal(true); return; }
        if (!aiScript || !editingItem) { setAiError("保存するスクリプトがありません。"); return; }

        setIsSavingHistory(true); setAiError(null); setAiSaveStatus(null);

        try {
            const payload = {
                deal_id: editingItem.deal_id || null,
                client_id: selectedClientId,
                sales_list_id: editingItem.id,
                list_type: editingItem.list_type,
                script_content: aiScript
            };
            
            const res = await fetch(`${API_URL}/api/deals/ai-script/save`, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...getAuthHeader() },
                body: JSON.stringify(payload)
            });
            
            const data = await res.json();
            if (!res.ok || !data.success) throw new Error(data.error || "AIスクリプトの保存に失敗しました。");
            
            setAiSaveStatus({ type: 'success', message: "AIスクリプトを保存しました。" });
            setAiError(null);
            fetchDeals(currentPage, activeFilters);

        } catch (err) {
            setAiError(`AIスクリプトの保存に失敗しました: ${err.message}`);
        } finally {
            setIsSavingHistory(false);
        }
    };

    const handleShowQuotationForm = useCallback(async () => {
        if (!modalDetails || !selectedClientId) return;
        if (isFreePlan) { setShowUpgradeModal(true); return; }

        setIsAiLoading(true); setAiError(null); setGeneratedQuotationUrl(null); setCopySuccess('');
        
        try {
            const customerName = modalDetails.details.company || "";
            const res = await fetch(`${API_URL}/api/company-profiles?client_id=${selectedClientId}&email=${user.email}&role=${user.role}`, { headers: getAuthHeader() });
            const data = await res.json();
            let issuerProfile = null;
            if (data.success && data.profiles.length > 0) issuerProfile = data.profiles[0];
            
            setQuotationForm({
                customer_name: customerName,
                issuer_name: issuerProfile?.company || user?.company || "",
                issuer_address: issuerProfile ? `${issuerProfile.pref || ''}${issuerProfile.city || ''}${issuerProfile.address1 || ''}${issuerProfile.address2 || ''}` : "",
                issuer_phone: issuerProfile ? `${issuerProfile.tel1 || ''}-${issuerProfile.tel2 || ''}-${issuerProfile.tel3 || ''}` : user?.phone || "",
                issuer_staff_name: issuerProfile ? `${issuerProfile.last_name || ''} ${issuerProfile.first_name || ''}`.trim() : user?.name || "",
                notes: "※ お振込手数料は貴社にてご負担をお願いいたします。", 
            });
            setQuotationItems([{ id: 1, name: "", quantity: 1, unit_price: 0 }]);
            setShowQuotationForm(true);
        } catch (err) { setAiError("見積書フォームの初期化に失敗しました。"); } 
        finally { setIsAiLoading(false); }
    }, [modalDetails, selectedClientId, user, isFreePlan]);

    const handleQuotationFormChange = (e) => {
        const { name, value } = e.target;
        setQuotationForm(prev => ({ ...prev, [name]: value }));
    };

    const handleQuotationItemChange = (index, e) => {
        const { name, value } = e.target;
        const newItems = [...quotationItems];
        newItems[index] = { ...newItems[index], [name]: value };
        setQuotationItems(newItems);
    };

    const addQuotationItem = () => setQuotationItems(prev => [...prev, { id: Date.now(), name: "", quantity: 1, unit_price: 0 }]);
    const removeQuotationItem = (index) => { if (quotationItems.length > 1) setQuotationItems(prev => prev.filter((_, i) => i !== index)); };

    const calculatedQuotationTotals = useMemo(() => {
        const subtotal = quotationItems.reduce((acc, item) => acc + ((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0)), 0);
        const tax = Math.floor(subtotal * 0.10);
        return { subtotal, tax, total: subtotal + tax };
    }, [quotationItems]);

    const handleGenerateQuotation = async () => {
        if (isFreePlan) { setShowUpgradeModal(true); return; }
        if (!editingItem?.deal_id) { setAiError("見積書を発行するには、まず案件の「結果登録」を保存する必要があります。"); return; }
        
        setIsGeneratingQuotation(true); setAiError(null); setGeneratedQuotationUrl(null); setCopySuccess('');

        try {
            const payload = {
                deal_id: editingItem.deal_id,
                customer_name: quotationForm.customer_name,
                issuer: {
                    name: quotationForm.issuer_name,
                    address: quotationForm.issuer_address,
                    phone: quotationForm.issuer_phone,
                    staff_name: quotationForm.issuer_staff_name,
                },
                items: quotationItems.map(item => ({
                    name: item.name,
                    quantity: parseFloat(item.quantity) || 0,
                    unit_price: parseFloat(item.unit_price) || 0,
                })),
                subtotal_amount: calculatedQuotationTotals.subtotal,
                tax_amount: calculatedQuotationTotals.tax,
                total_amount: calculatedQuotationTotals.total,
                notes: quotationForm.notes,
            };

            const res = await fetch(`${API_URL}/api/deals/quotations/generate`, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...getAuthHeader() },
                body: JSON.stringify(payload)
            });

            if (res.ok) {
                const data = await res.json();
                if (data.success && data.viewToken) {
                    setGeneratedQuotationUrl(`${window.location.origin}/app/quotation/${data.viewToken}`);
                    setShowQuotationForm(false);
                    fetchQuotationHistory();
                } else throw new Error(data.error);
            } else throw new Error((await res.json()).error);

        } catch (err) { setAiError(`見積書の発行に失敗しました: ${err.message}`); } 
        finally { setIsGeneratingQuotation(false); }
    };

    const handleCopyQuotationUrl = (viewToken) => {
        if (isFreePlan) { setShowUpgradeModal(true); return; }
        const url = `${window.location.origin}/app/quotation/${viewToken}`;
        try {
            const textArea = document.createElement("textarea");
            textArea.value = url;
            textArea.style.position = "fixed";
            textArea.style.opacity = 0;
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            const successful = document.execCommand('copy');
            document.body.removeChild(textArea);
            
            if (successful) {
                setCopySuccess('コピーしました！');
                setTimeout(() => setCopySuccess(''), 2000);
            } else {
                throw new Error('コピー失敗');
            }
        } catch (err) {
            setCopySuccess('コピー失敗');
            window.prompt("コピーに失敗しました。以下のURLを手動でコピーしてください:", url);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleManualSearch = () => {
        setHasSearched(true);
        setCurrentPage(1);
        setActiveFilters(isEnglishBeta ? getEnglishBetaDealFilters(filters) : filters);
    };

    const handlePageChange = (newPage) => {
        if (isFreePlan && newPage !== 1) { setShowUpgradeModal(true); return; }
        if (newPage > 0 && pagination && newPage <= pagination.totalPages) setCurrentPage(newPage);
    };
    
    const getStatusColorClass = (status) => {
        switch (status) {
            case "アポ": return "bg-sky-100 text-sky-800";
            case "商談": return "bg-amber-100 text-amber-800";
            case "受注": return "bg-green-100 text-green-800";
            case "失注": case "クレーム": return "bg-red-100 text-red-800";
            default: return "bg-gray-100 text-gray-800";
        }
    };
    const getAngleColorClass = (angle) => {
        switch (angle) {
            case "S": return "bg-blue-200 text-blue-800";
            case "A": return "bg-cyan-200 text-cyan-800";
            case "B": return "bg-teal-200 text-teal-800";
            case "C": return "bg-yellow-200 text-yellow-800";
            case "D": return "bg-orange-200 text-orange-800";
            case "E": return "bg-red-200 text-red-800";
            default: return "";
        }
    };

    const TabButton = ({ id, label, icon }) => (
        <button type="button" onClick={() => setActiveModalTab(id)} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${activeModalTab === id ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}>
            {icon}{label}
        </button>
    );

    const handleViewModeChange = (mode) => {
        setViewMode(mode);
        setCurrentPage(1);
        
        // "mine" の場合は自分のメールアドレスでフィルタリング、"all" の場合は解除
        if (mode === "mine") {
            const newFilters = isEnglishBeta ? getEnglishBetaDealFilters({ ...filters, sales_staff_email: user.email }) : { ...filters, sales_staff_email: user.email };
            setFilters(newFilters);
            setActiveFilters(newFilters);
            setHasSearched(true); // 検索実行状態にする
        } else {
            const newFilters = isEnglishBeta ? getEnglishBetaDealFilters({ ...filters, sales_staff_email: "" }) : { ...filters, sales_staff_email: "" };
            setFilters(newFilters);
            setActiveFilters(newFilters);
            setHasSearched(true);
        }
    };

    if (initialLoading) return <div className="flex items-center justify-center h-[80vh]"><LoadingSpinner text={isEnglishBeta ? "Preparing pipeline..." : "ページを準備しています..."} /></div>;

    return (
        <div className="p-4 md:p-8 bg-gray-50 min-h-full pb-24">
            <div className="max-w-screen-2xl mx-auto">
                <div className="mb-6">
                    <h1 className="text-3xl font-bold text-gray-800">{isEnglishBeta ? "Engagement Pipeline" : "📦 案件管理"}</h1>
                    {isEnglishBeta && (
                        <p className="text-sm text-gray-600 mt-2 max-w-3xl">
                            Track engaged Japanese companies after outreach, assign follow-up owners, and keep operator-approved next steps visible without opening the deeper Japanese CRM surface.
                            Company data is preserved in its original Japanese format for Japan-native follow-up.
                        </p>
                    )}
                </div>
                
                {/* View Mode Tabs */}
                <div className="flex space-x-4 mb-6 border-b border-gray-200">
                    <button onClick={() => handleViewModeChange("all")} className={`pb-3 px-4 font-medium text-sm transition-colors ${viewMode === "all" ? "border-b-2 border-blue-600 text-blue-600" : "text-gray-500 hover:text-gray-700"}`}>{isEnglishBeta ? "All Companies" : "📂 全ての案件"}</button>
                    <button onClick={() => handleViewModeChange("mine")} className={`pb-3 px-4 font-medium text-sm transition-colors ${viewMode === "mine" ? "border-b-2 border-blue-600 text-blue-600" : "text-gray-500 hover:text-gray-700"}`}>{isEnglishBeta ? "My Follow-ups" : "👤 自分の担当案件"}</button>
                </div>

                <div className="bg-white p-4 rounded-lg shadow-md mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-7 gap-4 items-end">
                        <div><label className="text-sm font-medium text-gray-600 block mb-1">{isEnglishBeta ? "Pipeline Stage" : "ステータス"}</label><select name="status" value={filters.status} onChange={handleFilterChange} className="w-full p-2 border rounded-md bg-white"><option value="">{isEnglishBeta ? "All" : "すべて"}</option>{DEAL_STATUSES.map(s=><option key={s} value={s}>{getDealStatusLabel(s, isEnglishBeta)}</option>)}</select></div>
                        <div><label className="text-sm font-medium text-gray-600 block mb-1">{isEnglishBeta ? "Priority" : "確度"}</label><select name="angle" value={filters.angle} onChange={handleFilterChange} className="w-full p-2 border rounded-md bg-white"><option value="">{isEnglishBeta ? "All" : "すべて"}</option>{["S", "A", "B", "C", "D", "E"].map(a=><option key={a} value={a}>{a}</option>)}</select></div>
                        <div><label className="text-sm font-medium text-gray-600 block mb-1">{isEnglishBeta ? "Follow-up Status" : "架電"}</label><select name="call_status" value={filters.call_status} onChange={handleFilterChange} className="w-full p-2 border rounded-md bg-white"><option value="">{isEnglishBeta ? "All" : "すべて"}</option>{CALL_STATUSES.map(cs=><option key={cs} value={cs}>{getCallStatusLabel(cs, isEnglishBeta)}</option>)}</select></div>
                        <div><label className="text-sm font-medium text-gray-600 block mb-1">{isEnglishBeta ? "Engagement" : "クリック"}</label><select name="hasClick" value={filters.hasClick} onChange={handleFilterChange} className="w-full p-2 border rounded-md bg-white"><option value="">{isEnglishBeta ? "All" : "すべて"}</option><option value="yes">{isEnglishBeta ? "Clicked" : "あり"}</option><option value="no">{isEnglishBeta ? "No Click" : "なし"}</option></select></div>
                        <div>
                            <label className="text-sm font-medium text-gray-600 block mb-1">{isEnglishBeta ? "Owner" : "担当者"}</label>
                            <select name="sales_staff_email" value={filters.sales_staff_email} onChange={handleFilterChange} className="w-full p-2 border rounded-md bg-white disabled:bg-gray-100" disabled={viewMode === 'mine'}>
                                <option value="">{isEnglishBeta ? "All Operators" : "全員"}</option>{users.map(u=><option key={u.id} value={u.email}>{u.name}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-600 block mb-1">{isEnglishBeta ? "Execution Outcome" : "送信ステータス"}</label>
                            <select name="send_status" value={filters.send_status} onChange={handleFilterChange} className="w-full p-2 border rounded-md bg-white">
                                {(isEnglishBeta ? SEND_STATUS_OPTIONS_EN : SEND_STATUS_OPTIONS_JA).map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                            </select>
                        </div>
                        {isEnglishBeta ? (
                            <div>
                                <label className="text-sm font-medium text-gray-600 block mb-1">Source</label>
                                <select name="listTypeFilter" value="master" disabled className="w-full p-2 border rounded-md bg-gray-100 text-gray-600">
                                    <option value="master">SalesBreaker DB</option>
                                </select>
                            </div>
                        ) : (
                            <div>
                                <label className="text-sm font-medium text-gray-600 block mb-1">リスト種別</label>
                                <select name="listTypeFilter" value={filters.listTypeFilter} onChange={handleFilterChange} className="w-full p-2 border rounded-md bg-white">
                                    <option value="">すべて</option><option value="master">弊社提供リスト</option><option value="client">マイリスト</option>
                                </select>
                            </div>
                        )}
                        <div className="md:col-span-3 lg:col-span-2"><label className="text-sm font-medium text-gray-600 block mb-1">{isEnglishBeta ? "Search" : "フリーワード検索"}</label><input type="text" name="searchTerm" value={filters.searchTerm} onChange={handleFilterChange} placeholder={isEnglishBeta ? "Company name, notes..." : "会社名、コメント等..."} className="w-full p-2 border rounded-md"/></div>
                    </div>
                    <div className="mt-4 pt-4 border-t flex justify-stretch sm:justify-end">
                        <button onClick={handleManualSearch} disabled={isLoading} className="w-full sm:w-auto justify-center flex items-center gap-2 bg-blue-600 text-white font-semibold py-2 px-4 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-wait">
                            {isLoading ? <LoadingSpinner text={isEnglishBeta ? "Searching..." : "検索中..."} /> : <><SearchIcon /> {isEnglishBeta ? "Search Pipeline" : "検索実行"}</>}
                        </button>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow-md overflow-hidden">
                    <div className="overflow-auto relative md:max-h-[65vh]">
                        {isLoading && <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10"><LoadingSpinner text={isEnglishBeta ? "Loading pipeline..." : "一覧を読み込んでいます..."} /></div>}
                        <div className="divide-y md:hidden">
                            {!hasSearched && !isLoading ? (
                                <div className="p-8 text-center text-gray-500">{isEnglishBeta ? "Set filters and search the pipeline." : "検索条件を指定して、「検索実行」ボタンを押してください。"}</div>
                            ) : list.length === 0 && !isLoading ? (
                                <div className="p-8 text-center text-gray-500">{isEnglishBeta ? "No pipeline records matched these filters." : "条件に一致する案件はありませんでした。"}</div>
                            ) : (
                                list.map(item => {
                                    const isNg = !!item.is_ng;
                                    const hasClick = !!item.has_click;
                                    const latestSendDate = item.my_latest_sent_at ? (isEnglishBeta ? formatJstDateTime(item.my_latest_sent_at) : new Date(item.my_latest_sent_at).toLocaleDateString()) : (isEnglishBeta ? 'None' : 'なし');
                                    const nextActionDate = formatDateInputValue(item.next_action_date);
                                    const isAssignedToOther = item.sales_staff_email && item.sales_staff_email !== user.email;

                                    return (
                                        <div key={`${item.id}-${item.list_type}-mobile`} className={`p-4 ${isNg ? 'bg-red-50 text-gray-500' : isAssignedToOther ? 'bg-orange-50' : 'bg-white'} ${selectedIds.has(`${item.id}-${item.list_type}`) ? 'ring-2 ring-inset ring-blue-200' : ''}`}>
                                            <div className="flex items-start gap-3">
                                                <input type="checkbox" checked={selectedIds.has(`${item.id}-${item.list_type}`)} onChange={() => handleCheckboxChange(item.id, item.list_type)} className="mt-1 w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"/>
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex items-start justify-between gap-2">
                                                        <div className="min-w-0">
                                                            <p className="font-bold text-gray-900 break-words">{item.company}{isNg && <span className="ml-2 px-2 py-0.5 bg-red-200 text-red-800 text-xs font-bold rounded-full">NG</span>}</p>
                                                            <p className="mt-1 text-xs text-gray-500">{getSourceLabel(item.list_type, isEnglishBeta)} / {item.phone || '電話番号なし'}</p>
                                                        </div>
                                                        <button onClick={() => handleEditClick(item)} className="shrink-0 p-2 text-blue-600 hover:bg-blue-100 rounded-full"><EditIcon/></button>
                                                    </div>
                                                    <div className="mt-3 flex flex-wrap gap-2 text-xs">
                                                        <span className={`px-2 py-1 font-semibold rounded-full ${getStatusColorClass(item.status)}`}>{getDealStatusLabel(item.status, isEnglishBeta)}</span>
                                                        <span className={`px-2 py-1 font-semibold rounded-full ${getAngleColorClass(item.angle)}`}>{item.angle || '確度なし'}</span>
                                                        <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-700">{hasClick ? (isEnglishBeta ? 'Clicked' : 'クリックあり') : (isEnglishBeta ? 'No Click' : 'クリックなし')}</span>
                                                    </div>
                                                    <div className="mt-3 grid grid-cols-1 gap-2">
                                                        <select value={item.call_status || ""} onChange={(e) => handleQuickUpdate(item, 'call_status', e.target.value)} className="w-full text-sm p-2 border rounded bg-white focus:ring-blue-500 focus:border-blue-500">
                                                            <option value="">{isEnglishBeta ? "Follow-up: Not Set" : "架電: 未選択"}</option>{QUICK_CALL_STATUSES.map(cs=><option key={cs} value={cs}>{getCallStatusLabel(cs, isEnglishBeta)}</option>)}
                                                        </select>
                                                        <select value={item.sales_staff_email || ""} onChange={(e) => handleQuickUpdate(item, 'sales_staff_email', e.target.value)} className={`w-full text-sm p-2 border rounded focus:ring-blue-500 focus:border-blue-500 ${isAssignedToOther ? 'bg-orange-100 text-orange-800 font-bold' : 'bg-white'}`}>
                                                            <option value="">{isEnglishBeta ? "Owner: Unassigned" : "担当: 未設定"}</option>{users.map(u=><option key={u.id} value={u.email}>{u.name}</option>)}
                                                        </select>
                                                    </div>
                                                    <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-gray-500">
                                                        <div>{isEnglishBeta ? "Last" : "最終送信"}: {latestSendDate}</div>
                                                        <div>{isEnglishBeta ? "Next" : "次回"}: {nextActionDate || 'ー'}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                        <table className="hidden w-full text-sm text-left text-gray-600 md:table">
                            <thead className="bg-gray-100 text-gray-700 uppercase text-xs sticky top-0 z-10">
                                <tr>
                                    <th className="py-3 px-4 w-12"><input type="checkbox" onChange={handleSelectAll} checked={list.length > 0 && list.every(item => selectedIds.has(`${item.id}-${item.list_type}`))} className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"/></th>
                                    <th className="py-3 px-4 w-12"></th>
                                    <th className="py-3 px-4">{isEnglishBeta ? "Company" : "会社名"}</th>
                                    <th className="py-3 px-4">{isEnglishBeta ? "Source" : "リスト種別"}</th>
                                    <th className="py-3 px-4">{isEnglishBeta ? "Phone" : "電話番号"}</th>
                                    <th className="py-3 px-4">{isEnglishBeta ? "Stage" : "ステータス"}</th>
                                    <th className="py-3 px-4">{isEnglishBeta ? "Priority" : "確度"}</th>
                                    <th className="py-3 px-4">{isEnglishBeta ? "Follow-up" : "架電結果 (クイック)"}</th>
                                    <th className="py-3 px-4">{isEnglishBeta ? "Owner" : "担当 (クイック)"}</th>
                                    <th className="py-3 px-4">{isEnglishBeta ? "Engagement" : "クリック"}</th>
                                    <th className="py-3 px-4">{isEnglishBeta ? "Last Outreach (JST)" : "最終送信日"}</th>
                                    <th className="py-3 px-4">{isEnglishBeta ? "Next Step" : "次回アクション"}</th>
                                    <th className="py-3 px-4">{isEnglishBeta ? "Execution Outcome" : "送信フラグ"}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y">
                                {!hasSearched && !isLoading ? (
                                    <tr><td colSpan="13" className="text-center p-8 text-gray-500">{isEnglishBeta ? "Set filters and search the pipeline." : "検索条件を指定して、「検索実行」ボタンを押してください。"}</td></tr>
                                ) : list.length === 0 && !isLoading ? (
                                    <tr><td colSpan="13" className="text-center p-8 text-gray-500">{isEnglishBeta ? "No pipeline records matched these filters." : "条件に一致する案件はありませんでした。"}</td></tr>
                                ) : (
                                    list.map(item => {
                                        const isNg = !!item.is_ng;
                                        const hasClick = !!item.has_click;
                                        const latestSendDate = item.my_latest_sent_at ? (isEnglishBeta ? formatJstDateTime(item.my_latest_sent_at) : new Date(item.my_latest_sent_at).toLocaleDateString()) : (isEnglishBeta ? 'None' : 'なし');
                                        const nextActionDate = formatDateInputValue(item.next_action_date);
                                        const isAssignedToOther = item.sales_staff_email && item.sales_staff_email !== user.email;
                                        const rowClass = isNg ? 'bg-red-50 text-gray-500' : isAssignedToOther ? 'bg-orange-50' : 'hover:bg-gray-50';

                                        return (
                                            <tr key={`${item.id}-${item.list_type}`} className={rowClass}>
                                                <td className="py-3 px-4"><input type="checkbox" checked={selectedIds.has(`${item.id}-${item.list_type}`)} onChange={() => handleCheckboxChange(item.id, item.list_type)} className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"/></td>
                                                <td className="py-3 px-4"><button onClick={() => handleEditClick(item)} className="p-2 text-blue-600 hover:bg-blue-100 rounded-full"><EditIcon/></button></td>
                                                <td className="py-3 px-4 font-medium">{item.company}{isNg && <span className="ml-2 px-2 py-0.5 bg-red-200 text-red-800 text-xs font-bold rounded-full">NG</span>}</td>
                                                <td className="py-3 px-4">{item.list_type === 'client' ? <span className="font-semibold text-purple-700">{getSourceLabel(item.list_type, isEnglishBeta)}</span> : <span className="text-gray-500">{getSourceLabel(item.list_type, isEnglishBeta)}</span>}</td>
                                                <td className="py-3 px-4">{item.phone || 'ー'}</td>
                                                <td className="py-3 px-4"><span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColorClass(item.status)}`}>{getDealStatusLabel(item.status, isEnglishBeta)}</span></td>
                                                <td className="py-3 px-4"><span className={`px-2 py-1 text-xs font-semibold rounded-full ${getAngleColorClass(item.angle)}`}>{item.angle || 'ー'}</span></td>
                                                
                                                <td className="py-3 px-4">
                                                    <select value={item.call_status || ""} onChange={(e) => handleQuickUpdate(item, 'call_status', e.target.value)} className="w-32 text-xs p-1 border rounded bg-white focus:ring-blue-500 focus:border-blue-500">
                                                        <option value="">{isEnglishBeta ? "Not Set" : "未選択"}</option>{QUICK_CALL_STATUSES.map(cs=><option key={cs} value={cs}>{getCallStatusLabel(cs, isEnglishBeta)}</option>)}
                                                    </select>
                                                </td>

                                                <td className="py-3 px-4">
                                                    <select value={item.sales_staff_email || ""} onChange={(e) => handleQuickUpdate(item, 'sales_staff_email', e.target.value)} className={`w-32 text-xs p-1 border rounded focus:ring-blue-500 focus:border-blue-500 ${isAssignedToOther ? 'bg-orange-100 text-orange-800 font-bold' : 'bg-white'}`}>
                                                        <option value="">{isEnglishBeta ? "Unassigned" : "未設定"}</option>{users.map(u=><option key={u.id} value={u.email}>{u.name}</option>)}
                                                    </select>
                                                    {isAssignedToOther && <div className="text-[10px] text-orange-600 mt-1">{isEnglishBeta ? "Other owner" : "⚠️ 他担当"}</div>}
                                                </td>

                                                <td className="py-3 px-4">{hasClick ? (isEnglishBeta ? 'Clicked' : 'あり') : (isEnglishBeta ? 'No Click' : 'なし')}</td>
                                                <td className="py-3 px-4">{latestSendDate}</td>
                                                <td className="py-3 px-4">{nextActionDate || 'ー'}</td>
                                                <td className="py-3 px-4"><StatusBadge status={item.my_latest_status} isEnglishBeta={isEnglishBeta} /></td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                    <div className="p-4 border-t flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
                        <span className="text-sm text-gray-600">{pagination ? (isEnglishBeta ? `${pagination.totalItems.toLocaleString()} records` : `${pagination.totalItems.toLocaleString()} 件中`) : ''}</span>
                        <Pagination currentPage={currentPage} totalPages={pagination ? pagination.totalPages : 0} onPageChange={handlePageChange} isLoading={isLoading} isEnglishBeta={isEnglishBeta} />
                    </div>
                </div>
            </div>

            {/* Bulk Action Bar */}
            {selectedIds.size > 0 && (
                <div className="fixed bottom-3 left-3 right-3 sm:left-1/2 sm:right-auto sm:bottom-6 sm:-translate-x-1/2 bg-white border border-gray-200 shadow-2xl rounded-lg p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4 z-40 animate-slide-up">
                    <span className="font-bold text-gray-700">{isEnglishBeta ? `${selectedIds.size} selected` : `${selectedIds.size} 件選択中`}</span>
                    <div className="hidden h-6 w-px bg-gray-300 sm:block"></div>
                    <button onClick={() => handleBulkUpdate({ sales_staff_email: user.email })} disabled={isBulkProcessing} className="flex items-center justify-center gap-2 bg-blue-600 text-white text-sm font-bold py-2 px-4 rounded hover:bg-blue-700 disabled:bg-gray-400">
                        <UserIcon /> {isEnglishBeta ? "Assign to me" : "自分の担当にする"}
                    </button>
                    <div className="relative group">
                        <button className="bg-gray-100 text-gray-700 text-sm font-bold py-2 px-4 rounded hover:bg-gray-200 flex items-center gap-1">{isEnglishBeta ? "Change stage..." : "ステータス変更..."}</button>
                        <div className="absolute bottom-full mb-2 left-0 bg-white shadow-lg rounded border w-40 hidden group-hover:block">
                            {["アポ", "商談", "失注"].map(s => (<button key={s} onClick={() => handleBulkUpdate({ status: s })} className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-50">{getDealStatusLabel(s, isEnglishBeta)}</button>))}
                        </div>
                    </div>
                    <button onClick={() => setSelectedIds(new Set())} className="text-gray-500 hover:text-gray-800 text-sm sm:ml-2">{isEnglishBeta ? "Cancel" : "キャンセル"}</button>
                </div>
            )}
            
            {/* Edit Modal */}
            {editingItem && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-stretch sm:items-center z-50 p-0 sm:p-4">
                    <form onSubmit={handleSave} className="bg-white rounded-none sm:rounded-lg shadow-2xl w-full max-w-5xl h-full max-h-none sm:h-auto sm:max-h-[90vh] flex flex-col">
                        <div className="flex justify-between items-start gap-3 p-4 border-b flex-shrink-0">
                            <h2 className="text-lg sm:text-xl font-bold text-gray-800 break-words">{isEnglishBeta ? "Pipeline Detail" : "案件編集"}: {editingItem.company}</h2>
                            <button type="button" onClick={() => setEditingItem(null)} className="p-2 hover:bg-gray-200 rounded-full"><CloseIcon/></button>
                        </div>
                        <div className="border-b border-gray-200 flex-shrink-0">
                            <nav className="flex gap-2 px-3 sm:px-4 overflow-x-auto">
                                <TabButton id="result" label={isEnglishBeta ? "Follow-up" : "結果登録"} />
                                {AI_SALES_CONSULTATION_ENABLED && !isEnglishBeta && <TabButton id="ai_consult" label="AI営業相談" icon={<SparklesIcon />} />}
                                {!isEnglishBeta && <TabButton id="quotation" label="見積書" icon={<DocumentTextIcon />} />}
                                {!isEnglishBeta && editingItem.list_type === 'client' && <TabButton id="web_timeline" label="Web閲覧履歴" icon={<EyeIcon />} />} 
                                <TabButton id="info" label={isEnglishBeta ? "Company" : "企業情報"} />
                                <TabButton id="history" label={isEnglishBeta ? "Change History" : "対応履歴"} />
                                <TabButton id="send_history" label={isEnglishBeta ? "Outreach History" : "送信履歴"} />
                                <TabButton id="clicks" label={isEnglishBeta ? "Engagement" : "クリック履歴"} />
                            </nav>
                        </div>
                        <div className="p-4 sm:p-6 overflow-y-auto flex-grow">
                            {isModalLoading ? <div className="flex justify-center items-center h-full"><LoadingSpinner text={isEnglishBeta ? "Loading detail..." : "詳細情報を読み込んでいます..."} /></div> : modalDetails ? (
                                <>
                                    {activeModalTab === 'result' && (
                                        <div className="space-y-4">
                                            {modalDetails.details.sales_staff_email && modalDetails.details.sales_staff_email !== user.email && (
                                                <div className="bg-orange-50 border-l-4 border-orange-500 p-4 mb-4">
                                                    <div className="flex"><div className="ml-3"><p className="text-sm text-orange-700 font-bold">{isEnglishBeta ? `Current owner: ${getUserNameByEmail(modalDetails.details.sales_staff_email)}` : `⚠️ 注意: 現在、担当者は ${getUserNameByEmail(modalDetails.details.sales_staff_email)} さんになっています。`}</p></div></div>
                                                </div>
                                            )}
                                            {saveStatus && <div className={`p-3 rounded-md text-sm ${saveStatus.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>{saveStatus.message}</div>}
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div><label className="text-sm font-medium">{isEnglishBeta ? "Pipeline Stage" : "ステータス"}</label><select name="status" value={form.status} onChange={handleFormChange} className="w-full mt-1 p-2 border rounded-md">{DEAL_STATUSES.map(s=><option key={s} value={s}>{getDealStatusLabel(s, isEnglishBeta)}</option>)}</select></div>
                                                <div><label className="text-sm font-medium">{isEnglishBeta ? "Priority" : "確度"}</label><select name="angle" value={form.angle} onChange={handleFormChange} className="w-full mt-1 p-2 border rounded-md"><option value="">{isEnglishBeta ? "Not Set" : "未選択"}</option>{["S", "A", "B", "C", "D", "E"].map(a=><option key={a} value={a}>{a}</option>)}</select></div>
                                            </div>
                                            <div><label className="text-sm font-medium">{isEnglishBeta ? "Follow-up Status" : "架電ステータス"}</label><select name="call_status" value={form.call_status} onChange={handleFormChange} className="w-full mt-1 p-2 border rounded-md"><option value="">{isEnglishBeta ? "Not Set" : "未選択"}</option>{CALL_STATUSES.map(cs=><option key={cs} value={cs}>{getCallStatusLabel(cs, isEnglishBeta)}</option>)}</select></div>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div><label className="text-sm font-medium">{isEnglishBeta ? "Owner" : "営業担当"} <span className="text-red-500">*</span></label><select name="sales_staff_email" value={form.sales_staff_email} onChange={handleFormChange} className="w-full mt-1 p-2 border rounded-md"><option value="">{isEnglishBeta ? "Unassigned" : "未選択"}</option>{users.map(u => <option key={u.id} value={u.email}>{u.name}</option>)}</select></div>
                                                {!isEnglishBeta && <div><label className="text-sm font-medium">CS担当</label><select name="cs_staff_email" value={form.cs_staff_email} onChange={handleFormChange} className="w-full mt-1 p-2 border rounded-md"><option value="">未選択</option>{users.map(u => <option key={u.id} value={u.email}>{u.name}</option>)}</select></div>}
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div><label className="text-sm font-medium">{isEnglishBeta ? "Next Action Date" : "次回アクション日"}</label><input type="date" name="next_action_date" value={form.next_action_date || ""} onChange={handleFormChange} className="w-full mt-1 p-2 border rounded-md" /></div>
                                                <div><label className="text-sm font-medium">{isEnglishBeta ? "Next Action Note" : "次回アクションメモ"}</label><input type="text" name="next_action_note" value={form.next_action_note || ""} onChange={handleFormChange} className="w-full mt-1 p-2 border rounded-md" placeholder={isEnglishBeta ? "Follow-up plan" : "例: 8月に再アプローチ"} /></div>
                                            </div>
                                            <div><label className="text-sm font-medium">{isEnglishBeta ? "Operator Notes" : "コメント"}</label><textarea name="comment" value={form.comment} onChange={handleFormChange} rows="4" className="w-full mt-1 p-2 border rounded-md"></textarea></div>
                                        </div>
                                    )}
                                    {AI_SALES_CONSULTATION_ENABLED && !isEnglishBeta && activeModalTab === 'ai_consult' && (
                                        <div className="space-y-4">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
                                                <div>
                                                    <label className="text-sm font-medium text-gray-700 block mb-1">相談する商品 <span className="text-red-500">*</span></label>
                                                    <select name="selected_product" value={selectedProductId} onChange={(e) => setSelectedProductId(e.target.value)} className="w-full p-2 border rounded-md bg-white disabled:bg-gray-100" disabled={isAiLoading || clientProducts.length === 0}>
                                                        {clientProducts.length > 0 ? (<><option value="" disabled>-- 売り込む商品を選択 --</option>{clientProducts.map(product => (<option key={product.id} value={product.id}>{product.product_name}</option>))}</>) : (<option value="" disabled>先に「設定」→「商品管理」で商品を登録してください</option>)}
                                                    </select>
                                                </div>
                                                <div className="self-end"><button type="button" onClick={handleGenerateAiScript} disabled={!selectedProductId || isAiLoading || isSavingHistory || (aiConsultationCount >= 3 && !isFreePlan)} className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold py-2 px-4 rounded-md hover:from-purple-700 hover:to-indigo-700 disabled:bg-gray-400 disabled:cursor-wait">{isAiLoading ? "生成中..." : <><SparklesIcon /> AIに相談する</>}</button></div>
                                            </div>
                                            {aiSaveStatus && <div className={`p-3 rounded-md text-sm ${aiSaveStatus.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>{aiSaveStatus.message}</div>}
                                            {aiError && <div className="p-3 rounded-md text-sm bg-red-50 border border-red-200 text-red-700">{aiError}</div>}
                                            {aiConsultationCount >= 3 && !isFreePlan && <div className="p-3 bg-yellow-50 border border-yellow-200 text-yellow-700 rounded-md text-sm">AI相談の回数が上限（1案件につき3回）に達しました。</div>}
                                            <div><label className="text-sm font-medium text-gray-700 block mb-1">AIによる営業提案 (編集可)</label><textarea value={aiScript} onChange={(e) => setAiScript(e.target.value)} rows="15" className="w-full mt-1 p-3 border rounded-md bg-gray-50 font-mono text-sm" placeholder={aiConsultationCount >= 3 ? "AI相談の回数が上限に達しました。" : "「AIに相談する」ボタンを押すと、この企業へのテレアポスクリプトや商談の流れがここに生成されます。"}></textarea></div>
                                            <div className="text-right"><button type="button" onClick={handleSaveAiScript} disabled={!aiScript || isAiLoading || isSavingHistory || isFreePlan} className="flex items-center gap-2 bg-gray-600 text-white font-semibold py-2 px-4 rounded-md hover:bg-gray-700 disabled:bg-gray-300">{isSavingHistory ? "保存中..." : <><SaveIcon/> この内容を保存</>}</button></div>
                                        </div>
                                    )}
                                    {!isEnglishBeta && activeModalTab === 'quotation' && (
                                        <div className="space-y-6">
                                            {!showQuotationForm ? (
                                                <div>
                                                    <div className="flex justify-between items-center mb-4"><h3 className="text-lg font-semibold text-gray-800">見積書 発行履歴</h3><button type="button" onClick={handleShowQuotationForm} disabled={isQuotationLoading || !editingItem.deal_id} className="flex items-center gap-2 bg-green-600 text-white font-semibold py-2 px-4 rounded-md hover:bg-green-700 disabled:bg-gray-400" title={!editingItem.deal_id ? "先に見積書以外の案件情報を保存してください" : ""}><PlusIcon /> 新規作成する</button></div>
                                                    {generatedQuotationUrl && <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md"><label className="text-sm font-medium text-gray-700 block mb-2">発行された見積書URL:</label><div className="flex gap-2"><input type="text" readOnly value={generatedQuotationUrl} className="w-full p-2 border rounded-md bg-gray-100 text-gray-700"/><button type="button" onClick={() => handleCopyQuotationUrl(generatedQuotationUrl.split('/').pop())} className="flex items-center gap-2 bg-gray-200 text-gray-700 font-semibold py-2 px-3 rounded-md hover:bg-gray-300"><ClipboardIcon /> {copySuccess || 'コピー'}</button></div></div>}
                                                    {isQuotationLoading ? <p>履歴を読み込み中...</p> : quotationHistory.length === 0 ? <p className="text-sm text-gray-500">{editingItem.deal_id ? "発行履歴はありません。" : "見積書を発行するには、まず「結果登録」タブから案件情報を保存してください。"}</p> : <ul className="divide-y divide-gray-200 border rounded-md max-h-60 overflow-y-auto">{quotationHistory.map(q => (<li key={q.id} className="p-3 flex justify-between items-center"><div><p className="font-semibold text-gray-700">{q.quotation_number}</p><p className="text-sm text-gray-500">発行日: {new Date(q.issue_date).toLocaleDateString('ja-JP')} | 合計金額: {q.total_amount.toLocaleString()} 円</p></div><button type="button" onClick={() => handleCopyQuotationUrl(q.view_token)} className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 py-1 px-3 rounded-md"><LinkIcon /> URLをコピー</button></li>))}</ul>}
                                                </div>
                                            ) : (
                                                <div>
                                                    <div className="flex justify-between items-center mb-4"><h3 className="text-lg font-semibold text-gray-800">見積書 新規作成</h3><button type="button" onClick={() => setShowQuotationForm(false)} className="text-sm text-gray-600 hover:underline">← 履歴に戻る</button></div>
                                                    {aiError && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm mb-4">{aiError}</div>}
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 border rounded-md mb-6">
                                                        <div className="space-y-2"><label className="block text-sm font-medium text-gray-700">宛先 (編集可)</label><input type="text" name="customer_name" value={quotationForm.customer_name} onChange={handleQuotationFormChange} className="w-full p-2 border rounded-md" placeholder="〇〇株式会社 御中" /></div>
                                                        <div className="space-y-2"><label className="block text-sm font-medium text-gray-700">発行元 (編集可)</label><input type="text" name="issuer_name" value={quotationForm.issuer_name} onChange={handleQuotationFormChange} className="w-full p-2 border rounded-md" placeholder="自社名" /><input type="text" name="issuer_address" value={quotationForm.issuer_address} onChange={handleQuotationFormChange} className="w-full p-2 border rounded-md" placeholder="自社住所" /><input type="text" name="issuer_phone" value={quotationForm.issuer_phone} onChange={handleQuotationFormChange} className="w-full p-2 border rounded-md" placeholder="自社電話番号" /><input type="text" name="issuer_staff_name" value={quotationForm.issuer_staff_name} onChange={handleQuotationFormChange} className="w-full p-2 border rounded-md" placeholder="自社担当者名" /></div>
                                                    </div>
                                                    <div className="overflow-x-auto">
                                                        <table className="w-full min-w-[600px] text-sm">
                                                            <thead className="bg-gray-100"><tr><th className="p-2 text-left font-medium text-gray-600">品目・摘要</th><th className="p-2 text-right font-medium text-gray-600 w-24">数量</th><th className="p-2 text-right font-medium text-gray-600 w-32">単価 (円)</th><th className="p-2 text-right font-medium text-gray-600 w-32">金額 (円)</th><th className="p-2 w-12"></th></tr></thead>
                                                            <tbody className="divide-y">
                                                                {quotationItems.map((item, index) => (
                                                                    <tr key={item.id}>
                                                                        <td className="p-1"><input type="text" name="name" value={item.name} onChange={(e) => handleQuotationItemChange(index, e)} className="w-full p-2 border rounded-md" placeholder="例: 〇〇システム利用料" /></td>
                                                                        <td className="p-1"><input type="number" name="quantity" value={item.quantity} onChange={(e) => handleQuotationItemChange(index, e)} className="w-full p-2 border rounded-md text-right" min="0" /></td>
                                                                        <td className="p-1"><input type="number" name="unit_price" value={item.unit_price} onChange={(e) => handleQuotationItemChange(index, e)} className="w-full p-2 border rounded-md text-right" min="0" /></td>
                                                                        <td className="p-1 text-right pr-3 text-gray-800">{((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0)).toLocaleString()}</td>
                                                                        <td className="p-1 text-center"><button type="button" onClick={() => removeQuotationItem(index)} disabled={quotationItems.length <= 1} className="text-red-500 hover:text-red-700 disabled:opacity-30 p-1"><TrashIcon /></button></td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                    <button type="button" onClick={addQuotationItem} className="mt-2 flex items-center gap-1 text-sm text-blue-600 hover:underline"><PlusIcon /> 項目を追加</button>
                                                    <div className="grid grid-cols-2 gap-4 mt-6">
                                                        <div className="space-y-2"><label className="block text-sm font-medium text-gray-700">備考 (編集可)</label><textarea name="notes" value={quotationForm.notes} onChange={handleQuotationFormChange} rows="4" className="w-full p-2 border rounded-md"></textarea></div>
                                                        <div className="space-y-2 p-4 bg-gray-50 rounded-md">
                                                            <div className="flex justify-between items-center text-md"><span className="font-medium text-gray-600">小計</span><span className="font-semibold text-gray-800">¥ {calculatedQuotationTotals.subtotal.toLocaleString()}</span></div>
                                                            <div className="flex justify-between items-center text-md"><span className="font-medium text-gray-600">消費税 (10%)</span><span className="font-semibold text-gray-800">¥ {calculatedQuotationTotals.tax.toLocaleString()}</span></div>
                                                            <div className="flex justify-between items-center text-xl border-t pt-2 mt-2"><span className="font-bold text-gray-900">合計金額</span><span className="font-bold text-gray-900">¥ {calculatedQuotationTotals.total.toLocaleString()}</span></div>
                                                        </div>
                                                    </div>
                                                    <div className="text-right mt-6"><button type="button" onClick={handleGenerateQuotation} disabled={isGeneratingQuotation} className="flex items-center gap-2 bg-blue-600 text-white font-semibold py-2 px-5 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-wait">{isGeneratingQuotation ? <LoadingSpinner text="発行中..." /> : "見積書URLを発行する"}</button></div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    
                                    {!isEnglishBeta && activeModalTab === 'web_timeline' && (
                                        <div className="space-y-4">
                                            <h3 className="text-lg font-semibold text-gray-800">Web閲覧履歴 (リストID: {editingItem.id})</h3>
                                            <div className="bg-blue-50 border-l-4 border-blue-400 p-3 text-sm text-blue-700 mb-6">
                                                <p>💡 この企業が貴社サイトを閲覧した記録です。リード時代の足跡も含めて表示しています。</p>
                                            </div>
                                            {isWebTimelineLoading ? (
                                                <div className="flex justify-center py-10"><LoadingSpinner text="履歴を読み込み中..." /></div>
                                            ) : webTimeline.length > 0 ? (
                                                <div className="space-y-4">
                                                    {webTimeline.map((item, idx) => (
                                                        <div key={idx} className="flex gap-4 items-start bg-white border border-gray-100 p-4 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                                                            {/* アイコン: WEBアクセスかメールアクションかで出し分け */}
                                                            <div className="p-2 bg-gray-50 rounded-lg text-gray-400">
                                                                {item.type === 'web' ? <EyeIcon /> : <LinkIcon />}
                                                            </div>
                                                            <div className="flex-grow min-w-0">
                                                                <div className="flex justify-between items-center mb-1">
                                                                    <span className={`text-[10px] font-black uppercase ${item.type === 'web' ? 'text-blue-500' : 'text-purple-500'}`}>
                                                                        {item.type === 'web' ? 'Web Access' : 'Mail Action'}
                                                                    </span>
                                                                    <span className="text-[10px] text-gray-400 font-mono">
                                                                        {item.timestamp ? new Date(item.timestamp).toLocaleString('ja-JP') : '日時不明'}
                                                                    </span>
                                                                </div>
                                                                <p className="text-sm font-bold text-gray-800 truncate">{item.summary || '無題のページ'}</p>
                                                                {item.detail && (
                                                                    <a href={item.detail} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 mt-1 break-all hover:underline block">
                                                                        {item.detail}
                                                                    </a>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <p className="text-center text-gray-400 text-sm py-10 font-bold">閲覧履歴は見つかりませんでした。</p>
                                            )}
                                        </div>
                                    )}

                                    {activeModalTab === 'info' && <div className="space-y-2 text-sm grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2"><div><strong>{isEnglishBeta ? "Company" : "会社名"}:</strong> {modalDetails.details.company || 'ー'}</div><div><strong>{isEnglishBeta ? "Phone" : "電話番号"}:</strong> {modalDetails.details.phone || 'ー'}</div><div><strong>FAX:</strong> {modalDetails.details.fax || 'ー'}</div><div><strong>{isEnglishBeta ? "Postal Code" : "郵便番号"}:</strong> {modalDetails.details.postal_code || 'ー'}</div><div className="md:col-span-2"><strong>{isEnglishBeta ? "Address" : "住所"}:</strong> {modalDetails.details.address || 'ー'}</div><div className="md:col-span-2"><strong>{isEnglishBeta ? "Industry" : "業界業種"}:</strong> {modalDetails.details.industry_class || 'ー'}</div><div className="md:col-span-2"><strong>{isEnglishBeta ? "Business Tags" : "事業内容タグ"}:</strong> {modalDetails.details.business_tags || 'ー'}</div><div className="md:col-span-2"><strong>{isEnglishBeta ? "List Tags" : "リストタグ"}:</strong> {modalDetails.details.list_tags || 'ー'}</div><div><strong>{isEnglishBeta ? "Capital" : "資本金"}:</strong> {modalDetails.details.capital || 'ー'}</div><div><strong>{isEnglishBeta ? "Employees" : "従業員数"}:</strong> {modalDetails.details.employee_count || 'ー'}</div><div><strong>{isEnglishBeta ? "Established" : "設立年月日"}:</strong> {modalDetails.details.establishment_date || 'ー'}</div><div><strong>{isEnglishBeta ? "Representative" : "代表者名"}:</strong> {modalDetails.details.representative_name || 'ー'}</div><div className="md:col-span-2"><strong>{isEnglishBeta ? "Company URL" : "会社URL"}:</strong> {modalDetails.details.url ? <a href={modalDetails.details.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">{modalDetails.details.url}</a> : 'ー'}</div><div className="md:col-span-2"><strong>{isEnglishBeta ? "Contact Form URL" : "問い合わせURL"}:</strong> {modalDetails.details.contact_url ? <a href={modalDetails.details.contact_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">{modalDetails.details.contact_url}</a> : 'ー'}</div></div>}
                                    {activeModalTab === 'history' && <div className="space-y-2 text-sm">{modalDetails.dealHistory.length > 0 ? modalDetails.dealHistory.map(h => (<div key={h.id} className="p-2 border-b"><p><strong>{isEnglishBeta ? "Updated" : "日時"}:</strong> {isEnglishBeta ? formatJstDateTime(h.updated_at) : new Date(h.updated_at).toLocaleString('ja-JP')}</p><p><strong>{isEnglishBeta ? "Updated By" : "入力者"}:</strong> {getUserNameByEmail(h.staff_email)}</p><p><strong>{isEnglishBeta ? "Owner" : "営業担当"}:</strong> {getUserNameByEmail(h.sales_staff_email)}</p><p><strong>{isEnglishBeta ? "Change" : "変更"}:</strong> {isEnglishBeta ? "Stage" : "ステータス"}: {getDealStatusLabel(h.old_status, isEnglishBeta)} → {getDealStatusLabel(h.new_status, isEnglishBeta)}</p><p style={{ whiteSpace: 'pre-wrap' }}><strong>{isEnglishBeta ? "Notes" : "コメント"}:</strong> {h.comment}</p></div>)) : <p>{isEnglishBeta ? "No change history yet." : "変更履歴はありません。"}</p>}</div>}
                                    {activeModalTab === 'send_history' && <div className="space-y-2 text-sm"><h3 className="font-bold text-md mb-2">{isEnglishBeta ? "Outreach History" : "送信文面履歴"}</h3>{modalDetails.sendHistory.length > 0 ? (<ul className="list-disc pl-5 space-y-1">{modalDetails.sendHistory.map((log, index) => (<li key={index}><StatusBadge status={log.status} isEnglishBeta={isEnglishBeta} /><span className="font-semibold ml-2">{isEnglishBeta ? formatJstDateTime(log.sent_at) : new Date(log.sent_at).toLocaleString('ja-JP')}</span><p className="text-xs text-gray-600 pl-2">Template: {log.template_title || (isEnglishBeta ? 'Untitled' : 'タイトルなし')}</p>{log.status.toLowerCase() !== 'success' && <p className="text-xs text-red-500 pl-2">{isEnglishBeta ? "Reason" : "理由"}: {log.error_details}</p>}</li>))}</ul>) : <p>{isEnglishBeta ? "No outreach history for this company yet." : "この案件に対する送信履歴はありません。"}</p>}</div>}
                                    {activeModalTab === 'clicks' && <div className="space-y-2 text-sm"><h3 className="font-bold text-md mb-2">{isEnglishBeta ? "Engagement Events" : "クリック履歴"}</h3>{modalDetails.clickLogs.length > 0 ? (<ul className="list-disc pl-5 space-y-1">{modalDetails.clickLogs.map((log, index) => (<li key={index}><span className="font-semibold">{isEnglishBeta ? formatJstDateTime(log.timestamp) : new Date(log.timestamp).toLocaleString('ja-JP')}</span>: <a href={log.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline ml-2 break-all">{log.url}</a></li>))}</ul>) : <p>{isEnglishBeta ? "No engagement events for this company yet." : "この案件に対するクリック履歴はありません。"}</p>}</div>}
                                </>
                            ) : null}
                        </div>
                        <div className="p-4 bg-gray-50 border-t flex justify-stretch sm:justify-end flex-shrink-0">
                            {activeModalTab === 'result' && <button type="submit" className="w-full sm:w-auto justify-center flex items-center gap-2 bg-blue-600 text-white font-semibold py-2 px-4 rounded-md hover:bg-blue-700"><SaveIcon/> {isEnglishBeta ? "Save Pipeline Update" : "保存"}</button>}
                        </div>
                    </form>
                </div>
            )}
            {showUpgradeModal && <UpgradeModal onClose={() => setShowUpgradeModal(false)} user={user} isEnglishBeta={isEnglishBeta} accountPath={accountPath} />}
        </div>
    );
}

export default DealsPage;
